import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatStepper } from '@angular/material/stepper'
import {
  ActionFailed,
  Channel,
  compactObject,
  ConfirmDialogService,
  ConfirmDialogStatus,
  integer,
  isSomething,
  positive,
  Rental,
  TimePickerUtils,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { FormBuilder, Validators, FormControl } from '@angular/forms'
import {
  availablePropertyClassTypes,
  BdcContentV2Service,
  BdcFacilityState,
  BdcPropertyExtendedFacilityMetadataItem,
} from '@tokeet-frontend/channel-content'
import { catchError, finalize, switchMap } from 'rxjs/operators'
import { Store } from '@ngrx/store'
import * as lodash from 'lodash'
import { of } from 'rxjs'
import { OpenChannelWizardDialog } from '@tv3/store/overlay/overlay.actions'
@Component({
  selector: 'app-bdc-create-property-dialog',
  templateUrl: './bdc-create-property-dialog.component.html',
  styleUrls: ['./bdc-create-property-dialog.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class BdcCreatePropertyDialogComponent implements OnInit {
  @ViewChild('stepper') stepper: MatStepper
  legalEntityId: number

  physicalAddressForm = this.fb.group({
    address_line: [null, [Validators.required]],
    city_name: [null, [Validators.required]],
    country_code: [null, [Validators.required]],
    postal_code: [null, [Validators.required]],
    display_address: [false],
  })
  positionForm = new FormControl(null, [Validators.required])

  checkInForm = this.fb.group({
    from: [null, [Validators.required]],
    until: [],
  })

  detailsForm = this.fb.group({
    property_name: [null, [Validators.required]],
    property_category: [null, [Validators.required]],
    check_in: this.checkInForm,
    room_count: [null, [Validators.required, integer(), positive()]],
  })

  addressForm = this.fb.group({
    physical_address: this.physicalAddressForm,
    position: this.positionForm,
  })

  isLoading = false
  tokeet2BDCAmenities: Record<string, BdcPropertyExtendedFacilityMetadataItem> = {}

  activeBdcAmenityIds: number[] = []

  // TV4-5909: the property can be created on Booking.com while its Tokeet connection
  // fails to configure -- the Complete step needs both to warn and offer recovery.
  createdPropertyId?: number
  connectionFailure?: { error?: string; error_code?: string } | null

  // TV4-6504: null while the approval check runs; false shows "Contact support" instead of the wizard
  creationApproved: boolean | null = null

  constructor(
    public dialogRef: MatDialogRef<BdcCreatePropertyDialogComponent>,
    private fb: FormBuilder,
    private toaster: Toaster,
    private confirm: ConfirmDialogService,
    private contentService: BdcContentV2Service,
    private store: Store<any>,
    @Inject(MAT_DIALOG_DATA) public data: { channel: Channel }
  ) {}

  ngOnInit(): void {
    // TV4-6504: tell a user who is not approved before the first step, not when the last step fails.
    // If the check itself fails, show the wizard: the channel manager still refuses the create request.
    this.contentService
      .getCreationApproval()
      .pipe(catchError(() => of({ approved: true })))
      .subscribe(({ approved }) => (this.creationApproved = approved))

    this.contentService.getPropertyAmenities().subscribe((amenities) => {
      this.tokeet2BDCAmenities = lodash.keyBy(amenities, (t) => t.tokeet_amenity)
    })
  }

  close() {
    this.dialogRef.close()
  }

  onRentalChange(rental: Rental) {
    const address = rental.address
    const property_category = availablePropertyClassTypes.find((t) => t.name === rental.type)?.code
    this.detailsForm.patchValue({
      property_name: rental.name,
      property_category,
      room_count: rental.bedrooms || undefined,
      check_in: {
        from: TimePickerUtils.formatTime(
          rental.checkin
            ? {
                hour: rental.checkin.hour,
                min: rental.checkin.min >= 30 ? 30 : 0,
              }
            : undefined,
          'HH:mm'
        ),
      },
    })

    this.addressForm.patchValue({
      physical_address: {
        address_line: address?.address,
        city_name: address?.city,
        country_code: address?.countryCode,
        postal_code: address?.postalCode || address?.zip,
      },
      position: {
        latitude: rental.gps?.lat,
        longitude: rental.gps?.long,
      },
    })

    this.confirm
      .openRegular({
        title: $localize`Copy Amenities from Rental`,
        body: $localize`Would you like to copy the amenities from the rental to the property?`,
      })
      .subscribe((status) => {
        if (status === ConfirmDialogStatus.Confirmed) {
          // tokeet amenities to bdc amenities
          this.activeBdcAmenityIds = lodash.compact(
            lodash
              .toPairs(rental.detail)
              .filter(([k, v]) => v)
              .map(([k]) => k)
              .map((k) => this.tokeet2BDCAmenities[k]?.facility_id)
          )
        } else {
          this.activeBdcAmenityIds = []
        }
      })
  }

  saveAmenities(propertyId: number) {
    if (!this.activeBdcAmenityIds?.length) {
      return of(null)
    }
    const data = this.activeBdcAmenityIds.map((id) => ({
      facility_id: id,
      state: BdcFacilityState.PRESENT,
    }))
    return this.contentService.updatePropertyFacilities(propertyId, data)
  }

  onSave() {
    const address = this.addressForm.value
    const details = this.detailsForm.value
    if (!isSomething(compactObject(address.position))) {
      this.toaster.error($localize`Please select a valid location for the property.`)
      return
    }
    this.isLoading = true
    this.contentService
      .createProperty(
        compactObject({
          ...details,
          ...address,
          legal_entity_id: this.legalEntityId,
        })
      )
      .pipe(
        finalize(() => (this.isLoading = false)),
        switchMap(({ property_id, connection }) => {
          // TV4-5909: record whether the backend actually configured the Tokeet connection
          // for the new property -- the Complete step warns and offers the connect wizard
          // when it did not, instead of pretending everything succeeded.
          this.createdPropertyId = property_id
          this.connectionFailure = connection && !connection.configured ? connection : null
          return this.saveAmenities(property_id)
        })
      )
      .subscribe(
        () => {
          this.stepper.next()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  // TV4-5909: recovery path when the property was created without its connection. The
  // overlay effect lazy-loads the connect wizard module, so this works from any context.
  onConnectNow() {
    this.store.dispatch(OpenChannelWizardDialog({ data: { channel: this.data.channel } }))
    this.close()
  }
}
